import React from 'react'

import MascotEars, { type MascotEarAnchor } from './mascot-ears'
import MascotFur from './mascot-fur'
import {
    flatArtOutline,
    MascotFlatEye,
    MascotHeadShading,
    MascotLimbPair,
    MascotPatternLayer,
    MascotPoseFrame,
    MascotSvgRoot,
    MASCOT_PEEK_VIEWBOX,
    type MascotRegion,
    type MascotSvgProps,
} from './mascot-svg-shared'

/**
 * Flat cat, ported from the illustrated asset set.
 *
 * The artwork was authored on a 512 grid, so it's kept verbatim inside a
 * `scale(0.25)` group rather than re-typed into the shared 128 space — the
 * artist's curves survive exactly, and only the head region below has to be
 * expressed in 128 units for patterns and clipping.
 *
 * The asset's own ears are deliberately dropped: they're replaced by the
 * shared ear library in `flat` mode so cats keep swappable ear styles.
 */

const ART_SCALE = 0.25

const HEAD: MascotRegion = { cx: 64, cy: 72, rx: 52, ry: 49, eyeY: 70 }
const EARS: MascotEarAnchor = {
    x: 37,
    y: 40,
    headCx: 64,
    scale: 1.34,
    rotate: 14,
    // A cat's mismatched look is one ear up and one folded — the default
    // pointed/drop pairing would give it a dog's ear.
    oneUpPair: ['pointed', 'folded'],
}
const PORTRAIT_VIEWBOX = '-4 -8 136 136'
const PEEK_TRANSFORM = 'translate(-14 -22) scale(1.22)'

/** Whiskers and the muzzle crease — the only "line work" in the flat set. */
const WHISKERS = [
    'M457.306 419.295C456.148 419.295 454.972 419.026 453.872 418.454C408.789 395.012 338.23 381.083 337.52 380.944C333.479 380.158 330.843 376.241 331.63 372.199C332.42 368.154 336.297 365.515 340.375 366.308C343.346 366.887 413.738 380.777 460.754 405.224C464.405 407.124 465.829 411.623 463.928 415.279C462.599 417.832 460 419.295 457.306 419.295Z',
    'M472.229 367.106C471.589 367.106 470.937 367.022 470.289 366.848C428.498 355.613 352.506 357.746 351.756 357.778C347.57 357.899 344.198 354.665 344.078 350.551C343.954 346.434 347.192 342.997 351.305 342.873C354.509 342.772 430.268 340.645 474.164 352.445C478.139 353.515 480.495 357.604 479.428 361.583C478.531 364.911 475.521 367.102 472.229 367.106Z',
    'M54.6916 419.295C51.9979 419.295 49.3978 417.835 48.0694 415.279C46.1686 411.624 47.5924 407.124 51.2437 405.224C98.2597 380.777 168.651 366.887 171.622 366.309C175.667 365.497 179.577 368.158 180.368 372.199C181.154 376.24 178.518 380.158 174.477 380.944C173.767 381.083 103.207 395.012 58.1251 418.455C57.0253 419.026 55.8499 419.295 54.6916 419.295Z',
    'M39.7687 367.106C36.4774 367.106 33.4669 364.911 32.5714 361.583C31.5049 357.604 33.8602 353.515 37.8355 352.445C81.7348 340.641 157.482 342.783 160.694 342.873C164.807 342.998 168.044 346.434 167.921 350.551C167.796 354.665 164.232 357.928 160.243 357.778C159.479 357.753 83.4754 355.612 41.71 366.848C41.0611 367.022 40.4095 367.106 39.7687 367.106Z',
]

/**
 * Eye placement, in portrait space. Taken from the centres of the asset's own
 * eye capsules, then drawn a little larger than the asset had them.
 */
const EYE = { y: 70.3, dx: 21.4, rx: 9, ry: 11.3 }

export default function CatMascot(props: MascotSvgProps) {
    const isPeek = props.pose === 'peek'
    return (
        <MascotSvgRoot
            {...props}
            viewBox={isPeek ? MASCOT_PEEK_VIEWBOX : PORTRAIT_VIEWBOX}
        >
            {ids => (
                <MascotPoseFrame
                    pose={props.pose}
                    ids={ids}
                    peekTransform={PEEK_TRANSFORM}
                    paws={<MascotLimbPair kind="paw" />}
                >
                    <defs>
                        <clipPath id={ids.headClip}>
                            <ellipse
                                cx={HEAD.cx}
                                cy={HEAD.cy}
                                rx={HEAD.rx}
                                ry={HEAD.ry}
                            />
                        </clipPath>
                    </defs>

                    <MascotFur furType={props.furType} region={HEAD} flat />
                    <MascotEars style={props.earStyle} anchor={EARS} flat />

                    <g transform={`scale(${ART_SCALE})`}>
                        {/* Head */}
                        <path
                            d="M255.999 486.4C371.301 486.4 464.772 397.938 464.772 288.814C464.772 179.69 371.301 91.228 255.999 91.228C140.697 91.228 47.2261 179.69 47.2261 288.814C47.2261 397.938 140.697 486.4 255.999 486.4Z"
                            fill="var(--coat-primary)"
                            {...flatArtOutline}
                        />
                        {/* Cheek shadow. Painted as translucent black rather
                            than the asset's fixed gold so it reads correctly
                            on any coat colour. */}
                        <path
                            d="M308.192 471.488C192.891 471.488 99.4216 383.026 99.4216 273.902C99.4216 193.77 149.852 124.831 222.288 93.8291C123.023 109.086 47.2288 190.557 47.2288 288.814C47.2288 397.938 140.698 486.4 255.999 486.4C286.632 486.4 315.694 480.105 341.903 468.887C330.925 470.575 319.673 471.488 308.192 471.488Z"
                            fill="#000"
                            opacity={0.07}
                        />
                    </g>

                    <MascotPatternLayer
                        pattern={props.pattern}
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotHeadShading
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotEars
                        style={props.earStyle}
                        anchor={EARS}
                        flat
                        layer="front"
                    />

                    <g transform={`scale(${ART_SCALE})`}>
                        {WHISKERS.map(d => (
                            <path
                                key={d.slice(0, 24)}
                                d={d}
                                fill="var(--coat-line)"
                                opacity={0.5}
                            />
                        ))}

                        {/* Mouth + tongue */}
                        <path
                            d="M285.823 411.839V426.752C285.823 443.223 272.471 456.576 255.999 456.576C239.527 456.576 226.175 443.223 226.175 426.752V411.839L255.999 396.927L285.823 411.839Z"
                            fill="var(--coat-flesh)"
                        />
                        <path
                            d="M255.999 396.927L226.175 411.839V426.752C226.175 429.118 226.52 431.39 227.04 433.597C234.543 432.666 241.79 430.493 248.543 427.159V434.207C248.543 438.325 251.881 441.664 255.999 441.664C260.117 441.664 263.456 438.326 263.456 434.207V427.16C270.209 430.494 277.456 432.667 284.958 433.597C285.479 431.39 285.823 429.117 285.823 426.752V411.839L255.999 396.927Z"
                            fill="#000"
                            opacity={0.14}
                        />
                        <path
                            d="M293.28 419.295C282.733 419.295 272.568 416.16 263.892 410.229C259.246 407.055 252.755 407.055 248.102 410.229C239.43 416.16 229.265 419.295 218.718 419.295C205.885 419.295 193.525 414.548 183.914 405.93C180.848 403.178 180.593 398.463 183.342 395.397C186.091 392.332 190.795 392.077 193.875 394.826C206.395 406.061 225.989 407.288 239.689 397.917C249.446 391.254 262.556 391.254 272.306 397.917C286.01 407.284 305.607 406.053 318.128 394.83C321.19 392.085 325.9 392.332 328.657 395.405C331.406 398.47 331.147 403.185 328.082 405.934C318.469 414.551 306.113 419.295 293.28 419.295Z"
                            fill="var(--coat-line)"
                            opacity={0.45}
                        />

                        {/* Nose */}
                        <path
                            d="M272.464 335.076H239.534C232.425 335.076 228.489 343.317 232.958 348.846L249.424 369.216C252.808 373.402 259.19 373.402 262.575 369.216L279.041 348.846C283.509 343.317 279.574 335.076 272.464 335.076Z"
                            fill="var(--coat-eye)"
                        />
                    </g>

                    <MascotFlatEye
                        cx={HEAD.cx - EYE.dx}
                        cy={EYE.y}
                        rx={EYE.rx}
                        ry={EYE.ry}
                    />
                    <MascotFlatEye
                        cx={HEAD.cx + EYE.dx}
                        cy={EYE.y}
                        rx={EYE.rx}
                        ry={EYE.ry}
                    />
                </MascotPoseFrame>
            )}
        </MascotSvgRoot>
    )
}
